/**
 * paletteStepTypes.ts - Shared step type definitions for palette components
 *
 * Contains the step type definitions used by both NodePalette (sidebar)
 * and StepPaletteModal (modal popup). Extracted to avoid duplication and
 * ensure consistency.
 *
 * Related:
 *   - components/NodePalette.tsx - Sidebar palette using these definitions
 *   - components/StepPaletteModal.tsx - Modal palette using these definitions
 */

import { NODE_TYPES } from '../types';
import {
	actionItemTintColor,
	brandPrimary,
	brandSecondary,
	campaignTintColor,
	emailActivityTintColor,
	newsletterActivityTintColor,
	orange,
	waitGray,
} from '../../../styles/colors';

export type PaletteCategory = 'all' | 'communication' | 'tasks' | 'tags' | 'control';

export type VisualCategory =
	| 'email'
	| 'texting'
	| 'newsletter'
	| 'card'
	| 'task'
	| 'opportunity'
	| 'tag'
	| 'tagRemove'
	| 'doNothing'
	| 'conditional'
	| 'triggerAutomation';

export interface PaletteNode {
	type: string;
	visualCategory: VisualCategory;
	paletteCategory: PaletteCategory;
	label: string;
	description: string;
}

export interface CategoryFilter {
	id: PaletteCategory;
	label: string;
}

export const categoryFilters: CategoryFilter[] = [
	{ id: 'all', label: 'All' },
	{ id: 'communication', label: 'Communication' },
	{ id: 'tasks', label: 'Tasks' },
	{ id: 'tags', label: 'Tags' },
	{ id: 'control', label: 'Control Flow' },
];

export const paletteNodes: PaletteNode[] = [
	// Communication steps
	{
		type: NODE_TYPES.EMAIL_STEP,
		visualCategory: 'email',
		paletteCategory: 'communication',
		label: 'Email',
		description: 'Send a personalized email',
	},
	{
		type: NODE_TYPES.TEXTING_STEP,
		visualCategory: 'texting',
		paletteCategory: 'communication',
		label: 'Text Message',
		description: 'Send an SMS text message',
	},
	{
		type: NODE_TYPES.HTML_NEWSLETTER_STEP,
		visualCategory: 'newsletter',
		paletteCategory: 'communication',
		label: 'Newsletter',
		description: 'Send an HTML newsletter',
	},
	{
		type: NODE_TYPES.HANDWRITTEN_CARD_STEP,
		visualCategory: 'card',
		paletteCategory: 'communication',
		label: 'Handwritten Card',
		description: 'Send a physical handwritten card',
	},
	// Task steps
	{
		type: NODE_TYPES.ACTION_ITEM_STEP,
		visualCategory: 'task',
		paletteCategory: 'tasks',
		label: 'Action Item',
		description: 'Create a follow-up task',
	},
	{
		type: NODE_TYPES.OPPORTUNITY_STEP,
		visualCategory: 'opportunity',
		paletteCategory: 'tasks',
		label: 'Opportunity',
		description: 'Create or update an opportunity',
	},
	// Tag steps
	{
		type: NODE_TYPES.ADD_TAG_STEP,
		visualCategory: 'tag',
		paletteCategory: 'tags',
		label: 'Add Tag',
		description: 'Add a tag to contacts',
	},
	{
		type: NODE_TYPES.REMOVE_TAG_STEP,
		visualCategory: 'tagRemove',
		paletteCategory: 'tags',
		label: 'Remove Tag',
		description: 'Remove a tag from contacts',
	},
	// Control flow steps
	{
		type: NODE_TYPES.NO_ACTION_STEP,
		visualCategory: 'doNothing',
		paletteCategory: 'control',
		label: 'Do nothing',
		description: 'End this branch without taking action',
	},
	{
		type: NODE_TYPES.SWITCH_STEP,
		visualCategory: 'conditional',
		paletteCategory: 'control',
		label: 'Conditional Branch',
		description: 'Split into different paths based on conditions',
	},
	{
		type: NODE_TYPES.TRIGGER_AUTOMATION_STEP,
		visualCategory: 'triggerAutomation',
		paletteCategory: 'control',
		label: 'Start Automation',
		description: 'Trigger another automation for the same contact',
	},
];

/**
 * Step types that can be added after an existing node.
 * Same as paletteNodes since triggers are not in the palette.
 */
export const addableStepTypes = paletteNodes;

/**
 * Color configuration for each visual category.
 * Used for consistent styling across palette components.
 *
 * These colors match the canvas node border colors from stepTypeColors.ts
 * using the same color constants from colors.ts.
 */
export const categoryColors: Record<VisualCategory, { color: string; backgroundColor: string; borderColor: string }> = {
	email: { color: emailActivityTintColor, backgroundColor: '#FDF2F9', borderColor: '#F4C6E3' },
	texting: { color: brandSecondary, backgroundColor: '#EFF6FF', borderColor: '#B8D5FE' },
	newsletter: { color: newsletterActivityTintColor, backgroundColor: '#F4FAED', borderColor: '#C9E8A9' },
	card: { color: campaignTintColor, backgroundColor: '#F5EEFA', borderColor: '#D4B8E8' },
	task: { color: actionItemTintColor, backgroundColor: '#F4FAED', borderColor: '#C9E8A9' },
	opportunity: { color: actionItemTintColor, backgroundColor: '#F4FAED', borderColor: '#C9E8A9' },
	tag: { color: brandPrimary, backgroundColor: '#EBF4FF', borderColor: '#B3D4FF' },
	tagRemove: { color: orange, backgroundColor: '#FFF7ED', borderColor: '#FDDCBE' },
	doNothing: { color: waitGray, backgroundColor: '#F9F9F9', borderColor: '#E0E0E0' },
	conditional: { color: brandPrimary, backgroundColor: '#EBF4FF', borderColor: '#B3D4FF' },
	triggerAutomation: { color: brandPrimary, backgroundColor: '#EBF4FF', borderColor: '#B3D4FF' },
};
